// Attachment picker — the files an email carries.
//
// Each picked file signs, uploads straight to storage, and commits; only
// then does its row post the file's id with the form. The form refuses to
// send while an upload is still running, so a save never drops a file the
// agent can see. Rows come from the component's <template>, so the markup
// lives in one place: components.AttachmentPicker.
//
// The server checks every limit again on save; the checks here only spare
// the agent a round trip, and its refusals use the server's own wording.
//
// Targets: [data-attachment-picker].

import { grouped } from "./count_phrase.js";
import { formatBytes } from "./presentation_tus.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const GENERIC_ERROR = "Upload failed. Please try again.";

export function initAttachmentPicker(root = document) {
  root.querySelectorAll("[data-attachment-picker]").forEach((picker) => {
    if (!firstBind(picker)) return;
    wire(picker);
  });
}

function wire(picker) {
  const input = picker.querySelector("[data-attachment-input]");
  const list = picker.querySelector("[data-attachment-list]");
  const error = picker.querySelector("[data-attachment-error]");
  const errorText = picker.querySelector("[data-attachment-error-text]");
  const budget = picker.querySelector("[data-attachment-budget]");
  const budgetText = picker.querySelector("[data-attachment-budget-text]");
  const announce = picker.querySelector("[data-attachment-announce]");
  const template = picker.querySelector("[data-attachment-row-template]");
  if (!list || !error || !budget || !announce || !template) return;

  const form = picker.closest("form");
  const { typeErr, capErr } = picker.dataset;
  const maxFiles = Number(picker.dataset.maxFiles) || 0;
  const maxBytes = Number(picker.dataset.maxBytes) || 0;
  const budgetBytes = Number(picker.dataset.budgetBytes) || 0;
  // A read-only picker has no file input.
  const accepted = (input?.accept ?? "")
    .toLowerCase()
    .split(",")
    .filter(Boolean);
  // A composer's reach changes with its choices; message_recipients.js
  // sends each change. The rendered value covers the opening state.
  let people = Number(picker.dataset.recipients) || 0;

  // Each pending row's upload, so removing the row, or htmx removing the
  // picker, stops the transfer.
  const uploads = new Map();

  // A sender who cannot upload keeps a template's files and adds none.
  const readOnly = picker.hasAttribute("data-read-only");
  const rows = () => Array.from(list.querySelectorAll("[data-attachment-row]"));
  const totalBytes = () =>
    rows().reduce((sum, row) => sum + (Number(row.dataset.size) || 0), 0);
  const uploading = () =>
    rows().some((row) => row.hasAttribute("data-pending"));

  // The one live region. Clearing it first makes a repeat, the same
  // refusal twice, announce again; messages from one task go out together.
  let queued = [];
  const say = (text) => {
    if (!text) return;
    if (queued.length === 0) {
      announce.textContent = "";
      requestAnimationFrame(() => {
        announce.textContent = queued.join(" ");
        queued = [];
      });
    }
    queued.push(text);
  };

  const refuse = (text) => {
    errorText.textContent = text;
    error.hidden = !text;
    say(text);
  };

  const render = () => {
    if (input) input.disabled = maxFiles > 0 && rows().length >= maxFiles;
    if (readOnly) picker.hidden = rows().length === 0;

    const over =
      people > 0 && budgetBytes > 0 && totalBytes() * people > budgetBytes;
    // Worded as campaigns.CheckFanOut words the refusal it warns of.
    const text = over
      ? `These files are too large to send to ${grouped(people)} people. Keep attachments under ${formatCap(Math.floor(budgetBytes / people))} in total, or send to fewer people.`
      : "";
    budget.hidden = !over;
    if (budgetText.textContent !== text) {
      budgetText.textContent = text;
      say(text);
    }
  };

  const add = (files) => {
    refuse("");
    for (const file of Array.from(files || [])) {
      if (maxFiles > 0 && rows().length >= maxFiles) {
        refuse(capErr);
        break;
      }
      // Listed by its extension or its type, as Resolve judges it.
      const [, ext] = splitName(file.name);
      const listed =
        accepted.includes(ext.toLowerCase()) ||
        accepted.includes(file.type.toLowerCase());
      if (accepted.length > 0 && !listed) {
        refuse(`${file.name}: ${typeErr}`);
        continue;
      }
      if (maxBytes > 0 && totalBytes() + file.size > maxBytes) {
        refuse(capErr);
        continue;
      }
      upload(file);
    }
    input.value = "";
    render();
  };

  const upload = async (file) => {
    const contentType = file.type || "application/octet-stream";
    const row = template.content.firstElementChild.cloneNode(true);
    const size = row.querySelector("[data-attachment-size]");
    const remove = row.querySelector("[data-attachment-remove]");
    row.dataset.size = String(file.size);
    row.setAttribute("data-pending", "");
    nameRow(row, file.name);
    size.textContent = "Uploading…";
    remove.setAttribute("aria-label", `Cancel upload of ${file.name}`);
    const controller = new AbortController();
    const { signal } = controller;
    uploads.set(row, controller);
    list.appendChild(row);
    render();

    try {
      const signed = await postForm(
        picker.dataset.signUrl,
        {
          filename: file.name,
          content_type: contentType,
          file_size_bytes: String(file.size),
        },
        signal,
      );
      const put = await fetch(signed.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
          ...(signed.upload_headers || {}),
        },
        body: file,
        signal,
      });
      if (!put.ok) throw new Error(`upload returned ${put.status}`);
      // A modal closed by removing it fires no htmx cleanup. A file whose
      // picker is gone stays uncommitted, and the vacuum takes it.
      if (!picker.isConnected) return;
      const committed = await postForm(
        picker.dataset.commitUrl.replace("{id}", signed.attachment_id),
        {},
        signal,
      );

      // Cancelled after the commit: the file stays unattached, and the
      // vacuum takes it.
      if (!row.isConnected) return;
      fillRow(row, committed);
      row.removeAttribute("data-pending");
      say(`${committed.file_name} attached.`);
    } catch (err) {
      // A cancelled upload ends here too, and the agent already knows.
      if (!row.isConnected) return;
      console.error("attachment upload failed", err);
      row.remove();
      refuse(
        `Couldn't attach ${file.name}. ${err.userMessage || GENERIC_ERROR}`,
      );
    } finally {
      uploads.delete(row);
    }
    render();
  };

  input?.addEventListener("change", () => add(input.files));

  // The whole block takes a drop. Without this, a file dropped near the
  // picker makes the browser open it and lose the draft.
  const carriesFiles = (event) =>
    event.dataTransfer?.types.includes("Files") ?? false;
  let dragDepth = 0;
  // Read-only takes the drop, so the browser keeps the draft, but offers
  // no target for it.
  picker.addEventListener("dragenter", (event) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    if (readOnly) return;
    dragDepth++;
    picker.setAttribute("data-dragover", "");
  });
  picker.addEventListener("dragover", (event) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    if (readOnly) event.dataTransfer.dropEffect = "none";
  });
  picker.addEventListener("dragleave", () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) picker.removeAttribute("data-dragover");
  });
  picker.addEventListener("drop", (event) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    dragDepth = 0;
    picker.removeAttribute("data-dragover");
    if (!readOnly) add(event.dataTransfer.files);
  });

  list.addEventListener("click", (event) => {
    const remove = event.target.closest("[data-attachment-remove]");
    if (!remove) return;
    const row = remove.closest("[data-attachment-row]");
    // Read-only has no input to return to: the next file's remove, or the
    // template list once the picker hides empty.
    const next = (
      row.nextElementSibling || row.previousElementSibling
    )?.querySelector("[data-attachment-remove]");
    uploads.get(row)?.abort();
    row.remove();
    refuse("");
    render();
    if (!readOnly) {
      input.focus();
    } else if (next) {
      next.focus();
    } else {
      form?.querySelector("[data-message-template-select]")?.focus();
    }
  });

  // A closed composer or a swapped editor takes its uploads with it.
  picker.addEventListener("htmx:beforeCleanupElement", () => {
    uploads.forEach((controller) => controller.abort());
  });

  // A saved template replaces the files with its own, already committed.
  picker.addEventListener("attachment-picker:set-files", (event) => {
    uploads.forEach((controller) => controller.abort());
    list.replaceChildren();
    for (const file of event.detail?.files || []) {
      const row = template.content.firstElementChild.cloneNode(true);
      fillRow(row, file);
      list.appendChild(row);
    }
    refuse("");
    render();
  });

  form?.addEventListener("message:reach", (event) => {
    people = event.detail.reached;
    render();
  });

  // Send sits in the footer, often with the picker scrolled away, so the
  // refusal scrolls into view. A hidden picker, on a text or a step that
  // sends nothing, holds nothing back: the server drops its files.
  form?.addEventListener("htmx:beforeRequest", (event) => {
    if (event.detail.elt !== form || !uploading()) return;
    if (picker.offsetParent === null) return;
    event.preventDefault();
    refuse("Wait for your files to finish uploading.");
    error.scrollIntoView({ block: "nearest" });
  });

  render();
}

// splitName parts a file name into its stem and extension, as
// components.splitFileName does.
function splitName(name) {
  const dot = name.lastIndexOf(".");

  return dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ""];
}

// nameRow writes a file's name into its row: the stem truncates and the
// extension stays, so a long name keeps its type readable.
function nameRow(row, name) {
  const [stem, ext] = splitName(name);
  row.querySelector("[data-attachment-name]").title = name;
  row.querySelector("[data-attachment-stem]").textContent = stem;
  row.querySelector("[data-attachment-ext]").textContent = ext;
}

// fillRow turns a row into a committed file, as the commit endpoint
// answers it.
function fillRow(row, file) {
  const id = row.querySelector("input[type=hidden]");
  id.value = file.attachment_id;
  id.disabled = false;
  row.dataset.size = String(file.size_bytes);
  nameRow(row, file.file_name);
  row.querySelector("[data-attachment-size]").textContent = formatBytes(
    file.size_bytes,
  );
  row
    .querySelector("[data-attachment-remove]")
    .setAttribute("aria-label", `Remove ${file.file_name}`);
}

// postForm posts fields and returns the JSON answer. Every error the
// attachment endpoints answer with is written for the agent, so the error
// passes it on.
async function postForm(url, fields, signal) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(fields),
    signal,
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(`${url} returned ${res.status}`);
    err.userMessage = payload?.error;
    throw err;
  }

  return payload;
}

// formatCap words a limit as billing.FormatStorageCap does: a value that
// is whole before rounding drops its decimal; any other keeps one.
function formatCap(bytes) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const shown =
    unit === 0 || Number.isInteger(value) ? value : value.toFixed(1);

  return `${shown} ${units[unit]}`;
}
